/* eslint-disable eqeqeq */
/* eslint-disable no-useless-return */
/* eslint-disable no-else-return */
import React, { useEffect, useCallback, useState, Suspense } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { useParams, navigate, useLocation } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'

import { categoriesSelector } from '_modules/categories/selectors'
import useBoolean from '_hooks/use-toggle'
import { banksLoadingSelector } from '_modules/utils/selectors'
import Button from '_components/button'
import { getCategories } from '_modules/categories/actions'
import AddRegions from '_/components/accordion/add-regions'
import AccordionAddUser from '_/components/accordion/add-users'
import AddCategories from '_/components/accordion/add-provider/add-categories'
import AddSpecialSkills from '_/components/accordion/add-provider/add-special-skills'
import Svg, { ICON } from '_components/svg'
import IconButton from '_components/svg/icon-button'
import { providerSelector } from '_modules/provider/selectors'

import {
  getProvider,
  deleteProviderContract,
  createProviderContract,
} from '_modules/provider/actions'
import { ROUTES } from '_utils/constants'
import Loading from '_components/loading'
import RatingModal, { WARNING_MODAL } from '_components/modal/rating-modal'

import useStyles from './styles'
import {
  CompanyAside,
  CompanyData,
  CompanyAddress,
  CompanyPrincipalData,
  ProviderCurrentStatus,
  CPF_REGEX,
} from './company-accordions'
import CompanyBankData from './company-accordions/company-bank-data'
import ProviderMetrics from './company-accordions/provider-metrics'
import { getFinanceOptions } from '_/modules/finance/actions'
import { financeOptionsSelector } from '_/modules/finance/selectors'
import AddBlockedAgency from '_/components/accordion/add-blocked-Agency'
import AddAttachments from './provider-contracts'
import useRolePermission from '_hooks/use-role-permission'
import { userSelector } from '_/modules/authentication/selectors'

import { getGenericParameters } from '_modules/service-orders/actions'
import { useDialog } from '_/hooks/use-dialog'
import { validationCNPJ, validationCPF } from '_/utils/helpers'
import { cpfCnpjMask } from '../finance/payer-data/utils'
import { ProviderReferaDetailsModal } from './components/ProviderReferaDetailsModal'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { createCompany, updateCompanyById, getCompanyUsers } from '_/services/provider'
import { getBankList } from '_/modules/utils/actions'
import { parse } from 'query-string'
import { useToast } from '_/hooks/use-toast'

const ProviderBuildingLink = React.lazy(() =>
  import('_/components/accordion/provider-building-link')
)

const Provider = () => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { providerId } = useParams()

  const [isEditing, handleEdit] = useBoolean(!providerId)
  const [bank, setBank] = useState()
  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(!!providerId)
  const taxRegimeOptions = useSelector(financeOptionsSelector)?.taxRegimes
  const [ratingModal, setRatingModal] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
  })
  const [companyUsers, setCompanyUsers] = useState([])
  const [isReferaDetailsModalOpen, setIsReferaDetailsModalOpen] = useState(false)
  const user = useSelector(userSelector)
  const provider = useSelector(providerSelector)
  const services = useSelector(categoriesSelector)
  const isBanksLoading = useSelector(banksLoadingSelector)
  const { isAdmin, checkIfUserDoesNotPermission, isSAAS } = useRolePermission()
  const bankDataPermissions = user?.permissions?.[PERMISSIONS.BANK_DATA] || []
  const canReadBankData = bankDataPermissions.includes(PERMISSIONS_ACTIONS.READ)
  const canManageBankData = bankDataPermissions.includes(PERMISSIONS_ACTIONS.EDIT)

  const methods = useForm({
    mode: 'all',
  })
  const { register, control, watch, setValue, handleSubmit, getValues, setError, formState } =
    methods
  const formData = watch()

  const [attachments, setAttachments] = useState([])
  const [toDelete, setToDelete] = useState([])
  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()
  const { state, search } = useLocation()

  const showProviderBuildings = provider?.toJS()?.hasAgencyLinked

  const handleDefault = () => {
    register('cities')
    setValue('cities', provider?.cities?.toJS() || [])

    register('main_services')
    setValue('main_services', provider?.mainServices.toJS() || [])

    register('priority_for_emergencies')
    setValue('priority_for_emergencies', provider?.priorityForEmergencies || false)

    register('get_contract_options')
    setValue('get_contract_options', provider?.getContractOptions?.toJS() || [])

    register('main_blocked_agencies')
    setValue(
      'main_blocked_agencies',
      provider?.mainBlockedAgencies.toJS().map((blockedAgenciesId, index) => {
        return {
          id: blockedAgenciesId,
          name: provider?.mainBlockedAgenciesNames.toJS()[index],
        }
      }) || []
    )

    register('main_special_skills')
    setValue('main_special_skills', provider?.mainSpecialSkills.toJS() || [])

    setValue('name', provider?.name)
    setValue('person_responsible_name', provider?.personResponsibleName)
    setValue('phone', provider?.phone)
    setValue('contact_email', provider?.contactEmail)

    register('brandLogo')
    setValue('brandLogo', provider?.brandLogo || null)
    register('tax_regime')
    setValue('tax_regime', provider?.taxRegime || '')
    setValue('company_creation_date', provider?.companyCreationDateBr || '')
    setValue('number_of_employees', provider?.numberOfEmployees || '')
    setValue('cnpj', provider?.cnpj ? cpfCnpjMask(provider?.cnpj) : '')

    setValue('address', provider?.address || '')
    setValue('street_number', provider?.streetNumber || '')
    setValue('cep', provider?.cep || '')
    setValue('extra_address_info', provider?.extraAddressInfo || '')
    setValue('neighborhood', provider?.neighborhood || '')
    setValue('city', provider?.city || '')
    setValue('nearby_landmark', provider?.nearbyLandmark || '')
    setValue('state', provider?.state || '')

    setValue('bank', provider?.bank?.toJS().id || '')
    setValue('bank_account_owner_name', provider?.bankAccountOwnerName || '')
    setValue('bank_account_cpf_cnpj', provider?.bankAccountCpfCnpj || '')
    setValue('bank_account_number', provider?.bankAccountNumber || '')
    setValue('bank_account_digit', provider?.bankAccountDigit || '')
    setValue('bank_agency_number', provider?.bankAgencyNumber || '')
    setValue('bank_account_type', provider?.bankAccountType || '')
    setValue('pix_key', provider?.pixKey || '')
    setValue('pix_key_choice', provider?.pixKeyChoice || '')

    setValue('contract_attachments', provider?.contract_attachments?.toJS() || '')

    register('buildings')
    setValue('buildings', provider?.buildings?.toJS() || [])
    register('agency_average_rate')
    setValue('agency_average_rate', provider?.agencyAverageRate?.toJS() || {})
    register('general_average_rate')
    setValue('general_average_rate', provider?.generalAverageRate?.toJS() || {})
  }

  const verifyRequiredField = useCallback(() => {
    setRatingModal({
      isOpen: true,
      title: 'Atenção!',
      subTitle: 'Um ou mais dados do formulário possuem problemas.',
      type: WARNING_MODAL,
    })
  }, [setRatingModal])

  const handleSuccess = useCallback(
    (infoMessage = 'Registro salvo com sucesso.') => {
      showToast({
        message: infoMessage,
        type: 'success',
      })
    },
    [showToast]
  )

  const handleError = useCallback(
    (message = 'Ocorreu um erro ao salvar o registro.') => {
      showToast({
        message,
        type: 'error',
      })
    },
    [showToast]
  )

  const validRequiredFields = useCallback(data => {
    const isCpf = CPF_REGEX.test(data?.cnpj)

    if (isCpf) {
      return true
    }

    if (!data?.tax_regime) {
      return false
    }
    return true
  }, [])

  const handleAttachmentsChange = useCallback(newAttachments => {
    setAttachments(newAttachments?.attachments)
    setToDelete(newAttachments?.toDelete)
  }, [])

  const normalizeFilename = filename => {
    const parts = filename.split('.')
    const ext = parts.length > 1 ? parts.pop() : ''
    const name = parts.join('.').replace(/[^a-zA-Z0-9]/g, '')
    return `${name}.${ext}`
  }

  const normalizeValue = value =>
    value === null || value === undefined ? '' : String(value).trim()
  const normalizeDigits = value => normalizeValue(value).replace(/[^0-9]/g, '')
  const bankDataFields = [
    'bank',
    'bank_account_owner_name',
    'bank_account_cpf_cnpj',
    'bank_account_number',
    'bank_account_digit',
    'bank_agency_number',
    'bank_account_type',
    'pix_key',
    'pix_key_choice',
  ]

  const getProviderBankId = () => {
    const providerBank = provider?.bank?.toJS ? provider.bank.toJS() : provider?.bank
    return providerBank?.id || ''
  }

  const hasBankDataFields = data => {
    return bankDataFields.some(field => Object.prototype.hasOwnProperty.call(data || {}, field))
  }

  const hasAnyBankData = data => {
    return bankDataFields.some(field => normalizeValue(data?.[field]) !== '')
  }

  const hasBankDataChanges = data => {
    const comparisons = [
      { key: 'bank', current: getProviderBankId(), normalize: normalizeValue },
      {
        key: 'bank_account_owner_name',
        current: provider?.bankAccountOwnerName,
        normalize: normalizeValue,
      },
      {
        key: 'bank_account_cpf_cnpj',
        current: provider?.bankAccountCpfCnpj,
        normalize: normalizeDigits,
      },
      {
        key: 'bank_account_number',
        current: provider?.bankAccountNumber,
        normalize: normalizeDigits,
      },
      {
        key: 'bank_account_digit',
        current: provider?.bankAccountDigit,
        normalize: normalizeDigits,
      },
      {
        key: 'bank_agency_number',
        current: provider?.bankAgencyNumber,
        normalize: normalizeDigits,
      },
      {
        key: 'bank_account_type',
        current: provider?.bankAccountType,
        normalize: normalizeValue,
      },
      { key: 'pix_key', current: provider?.pixKey, normalize: normalizeValue },
      { key: 'pix_key_choice', current: provider?.pixKeyChoice, normalize: normalizeValue },
    ]

    return comparisons.some(({ key, current, normalize }) => {
      if (!Object.prototype.hasOwnProperty.call(data || {}, key)) {
        return false
      }
      return normalize(data?.[key]) !== normalize(current)
    })
  }

  const handleSave = async savedata => {
    const data = savedata

    if (data?.cnpj) {
      const isValid =
        data?.cnpj.replace(/[^0-9]/g, '').length === 14
          ? validationCNPJ(data?.cnpj)
          : validationCPF(data?.cnpj)
      if (!isValid) {
        setError('cnpj', {
          type: 'manual',
          message: 'CPF/CNPJ inválido.',
        })
        return
      }
    }

    const bankDataDirty = bankDataFields.some(field => formState?.dirtyFields?.[field])
    const bankDataChanged = hasBankDataFields(data)
      ? providerId
        ? bankDataDirty && hasBankDataChanges(data)
        : hasAnyBankData(data)
      : false
    if (!canManageBankData && bankDataChanged) {
      checkIfUserDoesNotPermission(PERMISSIONS.BANK_DATA, PERMISSIONS_ACTIONS.EDIT)
      return
    }

    if (data?.brandLogo instanceof File) {
      const renamedFile = new File([data?.brandLogo], normalizeFilename(data?.brandLogo?.name), {
        type: data?.brandLogo?.type,
        lastModified: data?.brandLogo?.lastModified,
      })
      data.brandLogo = renamedFile
    } else if (typeof data?.brandLogo === 'string') {
      delete data.brandLogo
    }

    const newAttachments = attachments.filter(item => item?.id === undefined)

    if (data?.street_number === '') {
      delete data.street_number
    }
    if (data?.company_creation_date === '') {
      delete data.company_creation_date
    }
    if (data?.phone) {
      data.phone = data.phone.replace(/[^A-Z0-9]/gi, '')
    }
    if (!validRequiredFields(data)) {
      verifyRequiredField()
      return
    } else {
      try {
        setLoading(true)

        const queryAgency = parse(search)?.intermediaria

        const isCpf = CPF_REGEX.test(data?.cnpj)
        if (isCpf || !data.tax_regime || data.tax_regime === '') {
          data.tax_regime = null
        }

        const editData = {
          ...data,
          main_blocked_agencies: data.main_blocked_agencies.map(item => item.id),
          buildings: data.buildings || [],
        }
        if (providerId && !bankDataChanged) {
          bankDataFields.forEach(field => delete editData[field])
        }

        if (queryAgency) {
          editData.agency_id = queryAgency
          editData.refera_creating_for_agency = true
        }

        delete editData.generate_contract_date

        if (providerId) {
          if (
            checkIfUserDoesNotPermission(PERMISSIONS.TRADESMAN_CONFIGS, [
              PERMISSIONS_ACTIONS.ADD,
              PERMISSIONS_ACTIONS.EDIT,
            ])
          ) {
            setLoading(false)
            return
          }
          await updateCompanyById(providerId, editData)
          handleSuccess()

          if (toDelete.length > 0) {
            toDelete.forEach(fileID => dispatch(deleteProviderContract(fileID)))
            setToDelete([])
          }

          if (newAttachments && newAttachments.length !== provider?.contract_attachments?.toJS()) {
            try {
              await dispatch(createProviderContract({ contractAttachments: newAttachments }))
            } catch (error) {
              handleError(
                'O registro foi salvo com sucesso. Mas ocorreu um erro ao anexar arquivos de contrato.'
              )
            } finally {
              setLoading(false)
            }
          }
          handleEdit()
        } else {
          // CREATE REQUEST
          if (
            checkIfUserDoesNotPermission(PERMISSIONS.TRADESMAN_CONFIGS, PERMISSIONS_ACTIONS.ADD)
          ) {
            setLoading(false)
            return
          }

          const companyInfoData = await createCompany(editData)

          handleEdit()
          if (companyInfoData?.redirectTo)
            navigate(`${ROUTES.PROVIDER}/${companyInfoData?.redirectTo}`, {
              state: {
                redirectedToAlreadyExistingProviderMessage: companyInfoData?.infoMessage,
              },
            })
          else {
            handleSuccess()
            navigate(`${ROUTES.PROVIDER}/${companyInfoData.id}`)
          }
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.error_message || error?.error_message || null
        verifyRequiredField()
        handleError(errorMessage)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleNotSave = () => {
    if (providerId) {
      handleDefault()
      handleEdit()
      return
    }
    navigate(ROUTES.MANAGE_PROVIDERS)
  }

  const handleBackNavigation = useCallback(() => {
    if (state?.fromAgencyProviderId) {
      navigate(`${ROUTES.AGENCY}/${state?.fromAgencyProviderId}/prestadores`)
      return
    }
    if (providerId) {
      navigate(ROUTES.MANAGE_PROVIDERS)
      return
    }
    navigate(-1)
  }, [providerId])

  const handleGetFinanceOptions = useCallback(() => {
    if (taxRegimeOptions?.length === 0) {
      dispatch(getFinanceOptions())
    }
  }, [dispatch, taxRegimeOptions])
  // --------- "GET" REQUESTS FOR SELECT OPTIONS
  useEffect(() => {
    dispatch(getBankList()).then(res => setBank(res.results))
  }, [])

  useEffect(() => {
    if (!services?.length) {
      dispatch(getCategories())
    }
  }, [dispatch, services?.length])

  useEffect(() => {
    if (providerId) {
      const params = { building_by_user: true }
      dispatch(getProvider(providerId, params))
    }
  }, [dispatch, providerId])

  useEffect(() => {
    if (providerId) {
      setUsersLoading(true)
      getCompanyUsers('provider_company_users')(providerId)
        .then(data => {
          const results = Array.isArray(data?.results) ? data.results : data
          const mapped = (results || []).map(item => ({
            ...item,
            id: item?.id,
            name: item?.personResponsibleName || item?.person_responsible_name || item?.name || '',
            email: item?.email || '',
            phone: item?.telefone || item?.phone || '',
            isActive: item?.isActive ?? item?.is_active ?? true,
            isAdmin: item?.isAdmin ?? item?.is_admin ?? false,
          }))
          setCompanyUsers(mapped)
        })
        .catch(() => setCompanyUsers([]))
        .finally(() => setUsersLoading(false))
    }
  }, [providerId])

  useEffect(() => {
    if (state?.redirectedToAlreadyExistingProviderMessage) {
      showToast({
        type: 'info',
        message: state?.redirectedToAlreadyExistingProviderMessage,
      })
    }
    handleGetFinanceOptions()
    dispatch(
      getGenericParameters({
        name: 'emergency_management',
      })
    )
  }, [])

  useEffect(() => {
    handleDefault()
  }, [provider, handleEdit])
  // --------------------------------------------

  if (!provider && isBanksLoading) {
    return <Loading />
  }

  const serviceToAddSpacialSkills = getValues('main_services')

  const isCsRefera = user?.getRoles?.includes('cs_refera')

  const onEditButtonClick = () => {
    if (provider?.messageNotAllowedToUpdate) {
      showDialog({
        type: 'warning',
        title: 'Atenção!',
        subject: provider?.messageNotAllowedToUpdate,
        labelApprove: 'Ok',
        onApprove: closeDialog,
      })
      return
    }

    handleEdit()
  }

  return (
    <Grid className={styles.main}>
      <Grid container justify="center" alignItems="center" className={styles.save}>
        <IconButton
          buttonClassName={styles.navigate}
          aria-label="Voltar página"
          onClick={handleBackNavigation}
        >
          <ArrowBackIcon />
        </IconButton>
        <Grid className={styles.buttonWrapper}>
          {!isEditing ? (
            <div className={styles.headerButtonsContainer}>
              {isAdmin && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setIsReferaDetailsModalOpen(true)}
                  disabled={!provider || isBanksLoading}
                  style={{ padding: '10px 16px' }}
                >
                  Dados da Refera
                </Button>
              )}
              <Button onClick={onEditButtonClick} className={styles.buttonEdit} variant="outlined">
                Editar informações
                <Svg className={styles.buttonEditIcon} type={ICON.EDIT} />
              </Button>
            </div>
          ) : (
            <>
              <Typography variant="subtitle1" color="primary">
                Deseja salvar?
              </Typography>
              <Button
                className={styles.button}
                startIcon={loading ? <></> : <CheckIcon />}
                isLoading={loading}
                color="primary"
                variant="contained"
                type="submit"
                form="provider-form"
              >
                Sim
              </Button>
              <Button
                onClick={handleNotSave}
                startIcon={<CloseIcon />}
                color="primary"
                variant="outlined"
              >
                Não
              </Button>
            </>
          )}
        </Grid>
      </Grid>
      <FormProvider {...methods}>
        <form className={styles.main} id="provider-form" onSubmit={handleSubmit(handleSave)}>
          <Grid className={styles.container}>
            <Grid className={styles.aside}>
              <CompanyAside
                register={register}
                isEditing={isEditing}
                control={control}
                provider={provider}
                isCsRefera={isCsRefera}
              />
            </Grid>
            <Grid className={styles.accordionSection}>
              {isSAAS && <ProviderCurrentStatus />}
              <CompanyPrincipalData
                isEditing={isEditing}
                control={control}
                setValue={setValue}
                data={formData}
              />
              <CompanyData isEditing={isEditing} control={control} data={formData} />
              <CompanyAddress isEditing={isEditing} />
              <AccordionAddUser
                isProvider
                readOnly
                staffUsers={companyUsers}
                fallbackUser={
                  usersLoading
                    ? null
                    : {
                        name: formData?.person_responsible_name || provider?.personResponsibleName,
                        phone: formData?.phone || provider?.phone,
                        email: formData?.contact_email || provider?.contactEmail,
                      }
                }
                setFieldValue={setValue}
              />
              <AddRegions
                isProvider
                isEditing={isEditing}
                staffUsers={provider?.staffUsers?.valueSeq()?.toArray()}
                setValue={setValue}
              />
              <AddCategories
                isProvider
                setValue={newValue => {
                  setValue('main_services', newValue)
                }}
                control={control}
                isEditing={isEditing}
                data={formData?.main_services}
              />
              <AddSpecialSkills
                isProvider
                setValue={newValue => {
                  setValue('main_special_skills', newValue)
                }}
                control={control}
                isEditing={isEditing}
                data={formData?.main_special_skills}
                services={serviceToAddSpacialSkills}
              />
              {canReadBankData && (
                <CompanyBankData
                  register={register}
                  isEditing={isEditing && canManageBankData}
                  control={control}
                  provider={provider}
                  bank={bank}
                />
              )}
              {showProviderBuildings && (
                <Suspense fallback={<Loading />}>
                  <ProviderBuildingLink isEditing={isEditing} setValue={setValue} />
                </Suspense>
              )}
              {!isSAAS && (
                <AddBlockedAgency
                  isProvider
                  setValue={setValue}
                  control={control}
                  isEditing={isEditing}
                  data={formData?.main_blocked_agencies}
                  name="main_blocked_agencies"
                />
              )}
              <ProviderMetrics />

              <AddAttachments
                provider={provider}
                isEditing={isAdmin ? isEditing : !isEditing}
                control={control}
                onChange={handleAttachmentsChange}
              />
            </Grid>
            <RatingModal ratingModal={ratingModal} setRatingModal={setRatingModal} />
          </Grid>
          {isReferaDetailsModalOpen && (
            <ProviderReferaDetailsModal
              open={isReferaDetailsModalOpen}
              onClose={() => setIsReferaDetailsModalOpen(false)}
            />
          )}
        </form>
      </FormProvider>
    </Grid>
  )
}
export default Provider
